import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FaRegCalendarAlt } from "react-icons/fa";
import ResourceField from "../../ResourceField";
import WorkflowPanel from "../../workflow/WorkflowPanel";
import WorkflowRunPanel from "../../workflow/WorkflowRunPanel";
import AvailabilityGrid from "../AvailabilityGrid";
import { convertTo12Hour, formatDateDisplay, isPastDate } from "../bookingStepperUtils";
import TimeSlotPicker from "../TimeSlotPicker";

// Step 3: when and who - the date, the crew calendar, the optional bookable resource and
// the workflow (who does each step, shown in the order the steps happen).
function ScheduleStep({ b, formik }) {
  const {
    stepKey,
    editable,
    isEditing,
    group_id,
    crewLabel,
    dateSelect,
    open,
    setOpen,
    vendorBlockedDates,
    providerOptional,
    scheduleError,
    providerNotice,
    providerPanelOpen,
    setProviderPanelOpen,
    vendorSlots,
    pickDate,
    pickTime,
    removeProvider,
    timeSelect,
    providerSelected,
    isDataReady,
    totalReservedHours,
    showResourceField,
    activeResources,
    resourceIds,
    setResourceIds,
    resourceLabel,
    resourceTime,
    resourceDurationMin,
    resourceCurrentName,
    showRunWorkflow,
    runWorkflow,
    setRunWorkflow,
    formWorkflow,
    workflowInitial,
    workflowAssign,
    setWorkflowAssign,
    originalSnapshotRef
  } = b;
  const { setFieldValue } = formik;

  const mainPeople = [...new Set((providerSelected || []).map((p) => p?.title).filter(Boolean))];

  return (
    <>
      <div className="bstep-card" data-tour="booking-crew">
        <div className="bstep-card-head">
          <h5>When &amp; who</h5>
          {providerOptional ? (
            <p>Pick the day and a time. You can choose the {crewLabel.toLowerCase()} now or assign them later.</p>
          ) : (
            <p>Pick the day, then tap a free slot for the {crewLabel.toLowerCase()} below.</p>
          )}
        </div>

        <Row className="g-3">
          {/* Date */}
          <Col md={4} onClick={(e) => e.stopPropagation()}>
            <div className="d-flex flex-column">
              <Form.Label>
                Select Date
                <span style={{ color: "red", fontSize: "1rem" }}>*</span>
              </Form.Label>
              <div className="position-relative">
                <button
                  type="button"
                  className="btn d-flex align-items-center gap-2 px-3 position-relative"
                  style={{
                    height: "40px",
                    fontSize: "14px",
                    borderRadius: "6px",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E3EBF8",
                    color: "#000000",
                    justifyContent: "space-between",
                    width: "100%"
                  }}
                  disabled={!editable}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(!open);
                  }}
                >
                  {formatDateDisplay(dateSelect)}
                  <FaRegCalendarAlt className="position-absolute end-0 mx-2" />
                </button>

                {open && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      zIndex: 9999,
                      backgroundColor: "#fff",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                      borderRadius: "12px",
                      padding: "12px",
                      width: "300px"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "10px",
                        fontSize: "12px",
                        padding: "6px 8px",
                        background: "#f8f9fb",
                        borderRadius: "8px"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ color: "#d01c4c", fontSize: "14px" }}>●</span>
                        <span>Vendor on Leave / Holiday</span>
                      </div>
                    </div>

                    <DatePicker
                      inline
                      selected={dateSelect}
                      onChange={pickDate}
                      dateFormat="dd-MM-yyyy"
                      excludeDates={vendorBlockedDates}
                      dayClassName={(date) => {
                        const isBlocked = vendorBlockedDates?.some((d) => d.toDateString() === date.toDateString());
                        return isBlocked ? "blocked-date" : undefined;
                      }}
                    />
                    {isPastDate(dateSelect) && (
                      <div style={{ marginTop: "8px", fontSize: "12px", color: "#8a5a00", background: "#fff6e0", borderRadius: "8px", padding: "6px 8px" }}>
                        Backdated booking: the customer and crew will not be notified.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Selected time (set by the slot picked below) */}
          <Col md={4}>
            <div className="d-flex flex-column" data-field="schedule-time">
              <Form.Label>
                Selected Time
                <span style={{ color: "red", fontSize: "1rem" }}>*</span>
              </Form.Label>
              <div className={`bstep-readonly${scheduleError ? " is-invalid" : ""}`} aria-live="polite">
                {convertTo12Hour(timeSelect) || <span className="bstep-muted">{providerOptional ? "Pick a time below" : "Pick a slot below"}</span>}
              </div>
              {scheduleError && (
                <div className="bstep-field-error" role="alert" data-testid="schedule-error">
                  {scheduleError}
                </div>
              )}
            </div>
          </Col>

          <Col md={4}>
            <div className="d-flex flex-column">
              <Form.Label>Total Hours Reserved</Form.Label>
              <Form.Control
                type="text"
                value={isDataReady ? totalReservedHours : ""}
                readOnly
                style={{ height: "40px", background: "#f8f9fa" }}
              />
            </div>
          </Col>

          {/* Optional service step: the time comes from the vendor's own time list */}
          {providerOptional && (
            <Col xs={12}>
              <TimeSlotPicker
                data={vendorSlots?.data}
                loading={vendorSlots?.loading}
                error={vendorSlots?.error}
                value={timeSelect}
                onChange={pickTime}
                disabled={!editable}
              />
            </Col>
          )}

          {/* Who is selected */}
          <Col xs={12}>
            <div className="d-flex flex-column">
              <Form.Label>
                {crewLabel}
                {providerOptional ? (
                  <span className="bstep-muted"> (optional)</span>
                ) : (
                  <span style={{ color: "red", fontSize: "1rem" }}>*</span>
                )}
              </Form.Label>
              <div className="bstep-readonly bstep-crew" aria-live="polite">
                {providerSelected?.length > 0 ? (
                  providerSelected.map((data, idx) => (
                    <span key={idx} className="bstep-crew-chip" title={data?.extendedProps?.search || ""}>
                      {data?.extendedProps?.search || data?.title || ""}
                      {providerOptional && editable && (
                        <button
                          type="button"
                          className="bstep-chip-x"
                          aria-label={`Remove ${data?.extendedProps?.search || data?.title || "provider"}`}
                          onClick={() => removeProvider(data?.extendedProps?.provider_id)}
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  ))
                ) : providerOptional ? (
                  <span className="bstep-muted">To be assigned later</span>
                ) : (
                  <span className="bstep-muted">Nobody selected yet - tap a slot below</span>
                )}
              </div>
              {providerOptional && providerNotice && (
                <div className="bstep-slot-note is-warning" role="status" data-testid="provider-notice">
                  {providerNotice}
                </div>
              )}
            </div>
          </Col>

          {/* Optional bookable resource - renders nothing when the vendor has none */}
          {showResourceField && (
            <ResourceField
              resources={activeResources}
              value={resourceIds}
              onChange={setResourceIds}
              label={resourceLabel}
              date={dateSelect}
              time={resourceTime}
              durationMin={resourceDurationMin}
              excludeGroupId={group_id}
              currentLabel={resourceCurrentName}
              disabled={!editable}
              colMd={6}
            />
          )}
        </Row>
      </div>

      {/* Who does each step - only for a service with a workflow */}
      {(showRunWorkflow || formWorkflow) && (
        <div className="bstep-card">
          <Row>
            {isEditing && showRunWorkflow && (
              <WorkflowRunPanel
                variant="timeline"
                mirroredHint={providerOptional ? "assign later, or pick in the availability calendar" : "chosen from the availability grid below"}
                workflow={runWorkflow}
                onChanged={setRunWorkflow}
                disabled={!editable}
                assignments={workflowAssign}
                onAssign={(stepKey2, userId) => setWorkflowAssign((prev) => ({ ...prev, [stepKey2]: userId }))}
                mainPeople={mainPeople}
                date={dateSelect}
                time={resourceTime}
                durationMin={resourceDurationMin}
                excludeGroupId={group_id}
                onStatusChanged={(statusId) => {
                  // The workflow moved the booking's status: show it, and treat it as the saved
                  // value so Update doesn't write the old one back.
                  setFieldValue("status", statusId);
                  if (originalSnapshotRef.current) {
                    originalSnapshotRef.current.status = Number(statusId);
                  }
                }}
              />
            )}
            {formWorkflow && !(isEditing && showRunWorkflow) && (
              <WorkflowPanel
                variant="timeline"
                mirroredHint={providerOptional ? "Assign later, or pick in the availability calendar" : "Chosen from the availability grid below"}
                definition={formWorkflow}
                initialAssignments={workflowInitial}
                assignments={workflowAssign}
                onChange={setWorkflowAssign}
                resources={activeResources}
                resourceIds={resourceIds}
                resourceLabel={resourceLabel}
                mainPeople={mainPeople}
                date={dateSelect}
                time={resourceTime}
                durationMin={resourceDurationMin}
                excludeGroupId={group_id}
                disabled={!editable}
              />
            )}
          </Row>
        </div>
      )}

      <div className="bstep-card">
        {providerOptional ? (
          <details
            className="bstep-provider-panel"
            open={providerPanelOpen}
            onToggle={(e) => setProviderPanelOpen(e.currentTarget.open)}
          >
            <summary>Assign a {crewLabel.toLowerCase()} now (optional)</summary>
            <div className="mt-3">
              {/* Mounted only while this step shows and the panel is open: FullCalendar measures its container. */}
              {stepKey === "schedule" && providerPanelOpen && <AvailabilityGrid b={b} />}
            </div>
          </details>
        ) : (
          <>
            <div className="bstep-card-head">
              <h5>Availability</h5>
            </div>
            {/* Mounted only while this step shows: FullCalendar measures its container. */}
            {stepKey === "schedule" && <AvailabilityGrid b={b} />}
          </>
        )}
      </div>
    </>
  );
}

export default ScheduleStep;
